const EOCD_SIG = 0x06054b50
const CDFH_SIG = 0x02014b50
const LFH_SIG = 0x04034b50
const AUDIO_EXTS = new Set(['mp3', 'wav', 'aac', 'm4a'])

function isAudioName(name: string): boolean {
  const ext = name.toLowerCase().split('.').pop()
  return ext !== undefined && AUDIO_EXTS.has(ext)
}

function guessMime(name: string): string {
  const ext = name.toLowerCase().split('.').pop()
  if (ext === 'mp3') return 'audio/mpeg'
  if (ext === 'wav') return 'audio/wav'
  if (ext === 'aac') return 'audio/aac'
  if (ext === 'm4a') return 'audio/mp4'
  return 'application/octet-stream'
}

async function inflateRaw(compressed: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate-raw')
  const writer = ds.writable.getWriter()
  void writer.write(compressed as unknown as BufferSource)
  void writer.close()
  const buf = await new Response(ds.readable).arrayBuffer()
  return new Uint8Array(buf)
}

export async function extractZipAudio(file: File): Promise<File[]> {
  const ab = await file.arrayBuffer()
  const view = new DataView(ab)
  const bytes = new Uint8Array(ab)

  let eocd = -1
  const scanFrom = Math.max(0, ab.byteLength - (0xffff + 22))
  for (let i = ab.byteLength - 22; i >= scanFrom; i--) {
    if (view.getUint32(i, true) === EOCD_SIG) {
      eocd = i
      break
    }
  }
  if (eocd < 0) throw new Error('Not a valid zip (EOCD not found)')

  const totalEntries = view.getUint16(eocd + 10, true)
  const cdOffset = view.getUint32(eocd + 16, true)
  if (cdOffset === 0xffffffff) throw new Error('ZIP64 not supported')

  const textDecoder = new TextDecoder('utf-8')
  const results: File[] = []
  let p = cdOffset
  for (let i = 0; i < totalEntries; i++) {
    if (view.getUint32(p, true) !== CDFH_SIG) throw new Error('Malformed zip (CDFH signature)')
    const method = view.getUint16(p + 10, true)
    const compSize = view.getUint32(p + 20, true)
    const uncompSize = view.getUint32(p + 24, true)
    const nameLen = view.getUint16(p + 28, true)
    const extraLen = view.getUint16(p + 30, true)
    const commentLen = view.getUint16(p + 32, true)
    const localOffset = view.getUint32(p + 42, true)
    if (compSize === 0xffffffff || uncompSize === 0xffffffff || localOffset === 0xffffffff) {
      throw new Error('ZIP64 not supported')
    }
    const name = textDecoder.decode(bytes.subarray(p + 46, p + 46 + nameLen))
    p += 46 + nameLen + extraLen + commentLen

    if (name.endsWith('/')) continue
    const shortName = name.split('/').pop() ?? name
    if (shortName.startsWith('.') || shortName.startsWith('__MACOSX')) continue
    if (!isAudioName(shortName)) continue

    if (view.getUint32(localOffset, true) !== LFH_SIG) {
      throw new Error('Malformed zip (LFH signature)')
    }
    const lfNameLen = view.getUint16(localOffset + 26, true)
    const lfExtraLen = view.getUint16(localOffset + 28, true)
    const dataStart = localOffset + 30 + lfNameLen + lfExtraLen
    const compressed = bytes.subarray(dataStart, dataStart + compSize)

    let data: Uint8Array
    if (method === 0) {
      data = compressed
    } else if (method === 8) {
      data = await inflateRaw(compressed)
    } else {
      throw new Error(`Unsupported compression method ${method} for ${name}`)
    }

    results.push(new File([data as BlobPart], shortName, { type: guessMime(shortName) }))
  }
  return results
}

export function isZipFile(file: File): boolean {
  if (file.type === 'application/zip' || file.type === 'application/x-zip-compressed') return true
  return file.name.toLowerCase().endsWith('.zip')
}
